// Le bureau (maquette « 06 · Desktop — tableau de bord ») : un cadre commun à tous les écrans sur grand écran
// (en-tête, navigation à gauche) et le brief en tableau de bord — la notification, les quêtes du jour et l'objectif
// au centre, le statut et les portails à droite. Les blocs sont ceux des écrans du téléphone ; seule la mise en page
// change. La quête est partagée : accepter ici démarre le chrono de l'objectif.

import { type MouseEvent, type ReactNode, useEffect, useState } from 'react';

import type { Etat, Moi } from '@alfred/shared';
import type { PropsEcran } from '../App';
import { Cloche, Fenetre, Icone, type Onglet, Pastille } from '../components';
import { aujourdhui, jourCourt, jourMois } from '../lib/format';
import { nouvelleQuete } from '../lib/quete';
import { VOCAB } from '../lib/theme';
import { EnRetard, LignesQuotidien, Propositions, useHorloge } from './Brief';
import { BandeauInstallation } from './Installer';
import { ListePortails } from './Portails';
import { ObjectifQuete, useQuete } from './Quete';
import { DebuffsStatut, PoidsStatut, ProgressionStatut, TuilesStatut } from './Statut';

const LARGEUR_BUREAU = 1024;

/**
 * Le point de rupture est décidé ici seulement, comme pour l'Atelier : la feuille de style n'a pas de media query
 * pour le bureau, elle habille les classes que ce composant pose. Mesuré par un `ResizeObserver` sur le document et
 * par l'événement `resize` (l'un ou l'autre manque selon la façon dont la fenêtre change de taille).
 */
export function useBureau(): boolean {
  const [bureau, setBureau] = useState(() => window.innerWidth >= LARGEUR_BUREAU);
  useEffect(() => {
    const mesurer = () => setBureau((document.documentElement.clientWidth || window.innerWidth) >= LARGEUR_BUREAU);
    mesurer();
    const observateur = new ResizeObserver(mesurer);
    observateur.observe(document.documentElement);
    window.addEventListener('resize', mesurer);
    return () => {
      observateur.disconnect();
      window.removeEventListener('resize', mesurer);
    };
  }, []);
  return bureau;
}

/** Un onglet dont la section est sur la page y défile ; sinon, il ouvre l'écran du téléphone. */
function defiler(id: string) {
  return (e: MouseEvent) => {
    const cible = document.getElementById(id);
    if (!cible) return;
    e.preventDefault();
    cible.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
}

export function Bureau({ etat, actions, moi, questionDuJour }: PropsEcran & { moi: Moi; questionDuJour?: ReactNode }) {
  const s = etat.seance;
  const [quete, setQuete] = useQuete(s);
  const nom = s ? `Séance ${s.lettre} — ${s.seance.titre}` : '';
  const courte = etat.versionCourte?.dureeMin ?? 12;

  let notification;
  if (!s) {
    notification = <p className="message">[Aucune séance au programme aujourd'hui.] <span className="muted">Les quêtes quotidiennes t'attendent.</span></p>;
  } else if (s.faite) {
    notification = <p className="message">[Quête accomplie : <strong>{nom}</strong>.] <span className="muted">{s.faite.duree} · {s.faite.ressenti}</span></p>;
  } else if (quete) {
    notification = (
      <p className="message">
        [Quête acceptée : <strong>{quete.version === 'courte' ? `${nom} (${courte} min)` : nom}</strong>.]{' '}
        <span className="muted">Une séance courte faite vaut mieux qu'une séance parfaite remise à demain.</span>
      </p>
    );
  } else {
    notification = (
      <p className="message">
        [{s.repli ? 'Quête en attente' : VOCAB.queteQuotidienne} : <strong>{nom}</strong> {s.repli ? 'attend encore' : 'est arrivée'}.]{' '}
        <span className="muted">{s.repli ? `${jourCourt(s.date)} · rattrapage` : s.heure} · ≈ {s.seance.duree_min} min · repli {courte} min</span>
      </p>
    );
  }

  return (
    <CadreBureau moi={moi} etat={etat} actif="brief">
        <main className="bureau-main">
          <section className="bureau-centre">
            {etat.erreurs.length > 0 && (
              <a className="bandeau-erreur" href="#/reglages">⚠ {etat.erreurs.length} élément(s) illisible(s) dans le cerveau : voir le détail.</a>
            )}
            <BandeauInstallation />

            <Fenetre className="bureau-notif">
              <span className="equerre hg" />
              <span className="equerre bd" />
              <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="exclam" style={{ width: 26, height: 26, fontSize: 15 }}>!</span>
                  <Pastille>{VOCAB.notification}</Pastille>
                </div>
                {notification}
              </div>
              {s && !s.faite && (quete ? (
                <button className="btn" onClick={() => setQuete(null)}>Annuler</button>
              ) : (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button className="btn principal" style={{ minHeight: 48, padding: '0 22px' }} onClick={() => setQuete(nouvelleQuete(s, 'complete', Date.now()))}>{VOCAB.accepter}</button>
                  <button className="btn" style={{ minHeight: 48 }} onClick={() => setQuete(nouvelleQuete(s, 'courte', Date.now()))}>Version {courte} min</button>
                </div>
              ))}
            </Fenetre>

            {questionDuJour}
            <Propositions etat={etat} actions={actions} />

            <div className="bureau-tuiles">
              {s && (
                <div className={`ligne${s.faite ? ' accomplie' : ''}`}>
                  <a className="corps" href="#/quete" style={{ color: 'inherit' }} onClick={defiler('bureau-quete')}>
                    <b>Séance {s.lettre} · {s.seance.titre}</b>
                    <small>{s.faite ? `Accomplie · ${s.faite.duree} · ${s.faite.ressenti}` : `${s.heure} · ≈ ${s.seance.duree_min} min`}</small>
                  </a>
                  <span className="compteur" style={{ padding: '0 8px' }}>[{etat.semaine.faites} / {etat.semaine.prevues}]</span>
                </div>
              )}
              <LignesQuotidien etat={etat} actions={actions} />
            </div>

            <EnRetard etat={etat} actions={actions} />

            {s && (
              <div id="bureau-quete" className="bureau-ancre">
                <ObjectifQuete etat={etat} actions={actions} s={s} quete={quete} setQuete={setQuete} integre />
              </div>
            )}
          </section>

          <aside className="bureau-cote">
            <section id="bureau-statut" className="fenetre bureau-ancre">
              <div className="bureau-titre">
                <span className="display">{VOCAB.statut}</span>
                <span className="muted" style={{ fontSize: 12 }}>{etat.profil.classe || '[ Classe à définir ]'} · [ titre à débloquer ]</span>
              </div>
              <PoidsStatut etat={etat} />
              <TuilesStatut etat={etat} />
              {etat.statut.debuffs.length > 0 && <span className="muted" style={{ fontSize: 11, letterSpacing: 2 }}>{VOCAB.debuffs}</span>}
              <DebuffsStatut etat={etat} />
              <ProgressionStatut etat={etat} legende={false} />
              <a href="#/pesee" style={{ fontSize: 13, alignSelf: 'flex-end', padding: '8px 0' }}>Saisir une pesée</a>
            </section>

            <section id="bureau-portails" className="bureau-cadre bureau-ancre">
              <div className="bureau-titre">
                <span className="display">{VOCAB.portails}</span>
                <span className="mono muted" style={{ fontSize: 11 }}>14 J</span>
              </div>
              <ListePortails date={etat.date} ouverts={etat.ouverts} jours={etat.portails} />
            </section>
          </aside>
        </main>
    </CadreBureau>
  );
}

/** L'onglet allumé pour une route : un écran secondaire allume celui d'où l'on y va. */
export function ongletDe(route: string): Onglet | null {
  if (['/alfred', '/memoire', '/assistant', '/journal'].includes(route)) return 'alfred';
  if (route === '/' || route === '/a-valider') return 'brief';
  if (route === '/quete' || route === '/notification') return 'quetes';
  if (route === '/portails') return 'portails';
  if (route.startsWith('/cerveau')) return 'cerveau';
  if (route === '/statut' || route === '/pesee') return 'statut';
  return null;
}

/**
 * Le cadre du bureau, commun à tous les écrans sur grand écran : l'en-tête et la navigation à gauche, qui remplacent
 * la barre du bas du téléphone. Quêtes, Portails et Statut défilent jusqu'à leur bloc quand il est sur la page (le
 * tableau de bord), sinon ils ouvrent leur écran. `etat` : null pour un membre sans le module Santé.
 */
export function CadreBureau({ moi, etat, actif, children }: { moi: Moi; etat: Etat | null; actif: Onglet | null; children: ReactNode }) {
  const heure = useHorloge();
  const prenom = etat?.profil.prenom ?? moi.membre.prenom;
  const pct = etat?.semaine.prevues ? (etat.semaine.faites / etat.semaine.prevues) * 100 : 0;

  const tous: [Onglet, string, Parameters<typeof Icone>[0]['nom'], string, string?][] = [
    ['alfred', '#/alfred', 'bulle', VOCAB.nav.alfred],
    ['brief', '#/', 'hexagone', VOCAB.nav.brief],
    ['quetes', '#/quete', 'plume', VOCAB.nav.quetes, 'bureau-quete'],
    ['portails', '#/portails', 'portail', VOCAB.nav.portails, 'bureau-portails'],
    ['cerveau', '#/cerveau', 'cerveau', VOCAB.nav.cerveau],
    ['statut', '#/statut', 'barres', VOCAB.nav.statut, 'bureau-statut'],
  ];
  const onglets = moi.modules.sante ? tous : tous.filter(([id]) => id !== 'quetes' && id !== 'statut');

  return (
    <div className="bureau">
      <header className="bureau-tete">
        <a className="marque" href="#/" style={{ color: 'inherit' }}>
          <span className="accent" style={{ display: 'flex' }}><Icone nom="hexagone" taille={28} /></span>
          <span>{VOCAB.systeme}</span>
        </a>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <span className="losange accent" style={{ width: 8, height: 8, boxShadow: '0 0 8px var(--sys-accent-glow)' }} />
            {VOCAB.joueur}&nbsp;<strong className="accent">{prenom}</strong>{etat && <>&nbsp;· Niv. {etat.statut.niveau}</>}
          </span>
          <span className="mono muted" style={{ fontSize: 13, letterSpacing: 1 }}>{jourMois(etat?.date ?? aujourdhui())} · {heure}</span>
          <span style={{ display: 'flex' }}>
            {moi.modules.atelier && <a className="carre44" href="#/atelier" aria-label="Atelier"><Icone nom="atelier" /></a>}
            <Cloche />
            <a className="carre44" href="#/reglages" aria-label="Réglages"><Icone nom="reglages" /></a>
          </span>
        </div>
      </header>

      <div className="bureau-corps">
        <nav className="bureau-nav" aria-label="Navigation">
          {onglets.map(([id, href, icone, libelle, cible]) => (
            <a key={id} href={href} aria-current={id === actif ? 'page' : undefined} onClick={cible ? defiler(cible) : undefined}>
              <Icone nom={icone} />
              {libelle}
            </a>
          ))}
          {etat?.phase && (
            <div className="bureau-phase">
              <span className="muted" style={{ fontSize: 11, letterSpacing: 2 }}>PHASE {etat.phase.numero} · {etat.phase.nom.toUpperCase()}</span>
              <div className="jauge"><div style={{ width: `${pct}%` }} /></div>
              <div className="mono muted" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span>Séances sem.</span><span>[{etat.semaine.faites} / {etat.semaine.prevues}]</span>
              </div>
            </div>
          )}
        </nav>
        {children}
      </div>
    </div>
  );
}
